import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Modal, Button } from "react-bootstrap";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { GeoSearchControl, OpenStreetMapProvider } from "leaflet-geosearch";
import "leaflet-geosearch/dist/geosearch.css";
import L from "leaflet";
import axios from "axios";

// ⚠️ IMPORTANT: Replace this with your actual MapTiler API Key
const MAPTILER_API_KEY = 'B2JfeW0SWfn3Np5CNTjI'; 

// --- Configuration & Utilities ---
const mapContainerStyle = { height: "400px", width: "100%" };
const defaultCenter = [24.4667, 54.3667]; // Abu Dhabi, UAE
const uaeBounds = [[22, 51], [27, 57]]; 

// Fix Leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png",
});

// Geocoding function (uses Nominatim for reverse lookup)
const geocodeLatLng = async (lat, lng) => {
  try {
    const res = await axios.get(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&accept-language=en`
    );
    return res.data.display_name || "Address not found";
  } catch {
    return "Geocoding failed";
  }
};

// --- Leaflet Helper Components ---

const MapUpdater = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom);
  }, [map, center, zoom]);
  return null;
};

const MapEvents = ({ onMapClick }) => {
  useMapEvents({ click: (e) => onMapClick(e.latlng) });
  return null;
};

const DraggableMarker = ({ position, onDragEnd }) => {
  const markerRef = useRef(null);
  const eventHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (marker) onDragEnd(marker.getLatLng());
      },
    }),
    [onDragEnd]
  );
  return <Marker draggable eventHandlers={eventHandlers} position={position} ref={markerRef} />;
};

const SearchBar = ({ onSearchSelect, searchLang = 'en' }) => {
  const map = useMap();

  useEffect(() => {
    const provider = new OpenStreetMapProvider({
      params: {
        'accept-language': searchLang, // Use the language state for search results
      },
    });

    const searchControl = new GeoSearchControl({
      provider,
      showMarker: true,
      showPopup: false,
      marker: {
        icon: new L.Icon.Default(),
      },
      style: 'bar',
      autoClose: true,
      keepResult: true,
      searchLabel: 'Search for location...',
    });
  
    map.addControl(searchControl);
  
    const handleSearchSelect = (event) => {
      const { location } = event;
      onSearchSelect(location);
    };
    map.on('geosearch/showlocation', handleSearchSelect);
  
    return () => {
      map.removeControl(searchControl);
      map.off('geosearch/showlocation', handleSearchSelect);
    };
  }, [map, onSearchSelect, searchLang]);

  return null;
};

// --- Main Map Modal Component ---

const MapPopup = ({ show, handleClose, title, center, setCoordinatesAndAddress, mapLanguage }) => {
  const [currentLocation, setCurrentLocation] = useState(center || defaultCenter);
  const [address, setAddress] = useState("");

  useEffect(() => {
    if (show && center) {
      setCurrentLocation(center);
      geocodeLatLng(center[0], center[1]).then(setAddress);
    } else if (show) {
      setCurrentLocation(defaultCenter); 
      setAddress("");
    }
  }, [show, center]);

  const handleMapClick = useCallback(async (latlng) => {
    setCurrentLocation([latlng.lat, latlng.lng]);
    setAddress(await geocodeLatLng(latlng.lat, latlng.lng));
  }, []);

  const handleDragEnd = useCallback(async (latlng) => {
    setCurrentLocation([latlng.lat, latlng.lng]);
    setAddress(await geocodeLatLng(latlng.lat, latlng.lng));
  }, []);

  const handleSearchSelect = useCallback(async (location) => {
    const newLatLng = [location.y, location.x];
    setCurrentLocation(newLatLng);
    setAddress(location.label);
  }, []);

  const handleSelectLocation = () => {
    // Pass precise coordinates back to the parent component
    setCoordinatesAndAddress([currentLocation[0].toFixed(6), currentLocation[1].toFixed(6)], address);
    handleClose();
  };

  // MapTiler URL with multilingual support
  const tileLayerUrl = `https://api.maptiler.com/maps/streets/{z}/{x}/{y}.png?key=${MAPTILER_API_KEY}&language=${mapLanguage}`;
  const tileLayerAttribution = '&copy; <a href="https://www.maptiler.com/">MapTiler</a> & ' + '<a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{title} (Language: {mapLanguage.toUpperCase()})</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <p><b>Coordinates:</b> {currentLocation[0].toFixed(6)}, {currentLocation[1].toFixed(6)}</p>
          <p><b>Address:</b> {address}</p>
          <Button variant="success" onClick={handleSelectLocation}>Select Location</Button>
        </div>
        <div style={mapContainerStyle}>
          <MapContainer 
            center={currentLocation} 
            zoom={13} 
            style={mapContainerStyle} 
            maxBounds={uaeBounds} 
            // Key forces map redraw when mapLanguage changes, ensuring the TileLayer updates
            key={mapLanguage} 
          >
            <TileLayer
              attribution={tileLayerAttribution}
              url={tileLayerUrl}
              minZoom={3} 
            />
            <MapUpdater center={currentLocation} zoom={13} />
            <SearchBar onSearchSelect={handleSearchSelect} searchLang={mapLanguage} />
            <DraggableMarker position={currentLocation} onDragEnd={handleDragEnd} />
            <MapEvents onMapClick={handleMapClick} />
          </MapContainer>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MapPopup;